import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabaseClient";
import type { DraftSnapshot } from "../../shared/draft";
import type { PricingItemRow } from "../../shared/pricing";
import { computeEstimate } from "../../shared/pricing";
import {
  buildEmailHtml,
  buildEmailText,
  buildEmailVars,
  emailFooterFromSettings,
  invokeSendProposalEmail,
  isSenderInfoComplete,
  renderEmailBody,
  renderTemplate,
  saveProposalAndGetLink,
  SENDER_INFO_MISSING_MESSAGE,
} from "../../shared/proposalSend";
import { enqueueEmail } from "../../emailQueue";

/* ============================================================
   MobileSendSheet
   ----------------------------------------------------------------
   Bottom-sheet send UI for the mobile proposal screen. Same flow
   desktop's "Email Proposal" runs (App.tsx:1907-handleEmailProposal):

     1. fetchProposalSectionsSnapshot(orgId)
     2. buildProposalPayload(snapshot + sections + userSettings + computed)
     3. saveOrUpdateProposal(supabase, payload, estimateName)
        → inserts or updates a proposals row (idempotent via
          du_proposal_id::{estimateName}) and returns its id
     4. buildReviewLink(id)
     5. buildEmailVars + renderTemplate for subject/body
     6. buildEmailHtml + buildEmailText (View Proposal button straight
        to /review + company footer; no tracking pixel or redirect)
     7. invokeSendProposalEmail({to, subject, html, proposalId, replyTo,
        text, cc}) — replyTo = userSettings.userEmail, cc = [replyTo]
        when "Send me a copy" is on.

   On offline or transient send failure, enqueueEmail() drops the send
   into the existing du_email_queue_v1 store so it can be flushed on
   the next online tick.

   The sheet is self-contained — it owns its own form state, status,
   and templates. The parent just passes the data it already has
   (snapshot, pricingItems, estimate, userSettings, orgId).
   ============================================================ */

export type MobileSendSheetProps = {
  open: boolean;
  onClose: () => void;
  snapshot: DraftSnapshot;
  pricingItems: PricingItemRow[];
  /** Output of computeEstimate — same object MobileProposalPage uses. */
  estimate: ReturnType<typeof computeEstimate>;
  orgId: string | null;
  userSettings: any;
  /**
   * Live notes value from MobileProposalPage's state. Forwarded to
   * buildProposalPayload as proposalNotesSnapshotOverride so the saved
   * proposal row carries the notes the user actually typed. Without this
   * the payload would fall back to a localStorage read keyed by
   * estimateName — and mobile's estimateName is usually "", which means
   * the key is unusable and the customer sees an empty Notes section.
   */
  proposalNotes?: string;
  /** Optional: when the send succeeds and the parent wants a toast. */
  onSent?: (info: { proposalId: string; reviewLink: string }) => void;
};

type Status =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "sent"; link: string }
  | { kind: "queued"; reason: string }
  | { kind: "failed"; message: string };

export default function MobileSendSheet({
  open,
  onClose,
  snapshot,
  pricingItems,
  estimate,
  orgId,
  userSettings,
  proposalNotes = "",
  onSent,
}: MobileSendSheetProps) {
  const client = useMemo(
    () => ({
      clientFirstName: snapshot.clientFirstName || "",
      clientLastName: snapshot.clientLastName || "",
      clientLocation: snapshot.clientLocation || "",
      clientTown: snapshot.clientTown || "",
      clientState: snapshot.clientState || "",
      clientZip: snapshot.clientZip || "",
      clientPhone: snapshot.clientPhone || "",
      clientEmail: snapshot.clientEmail || "",
    }),
    [
      snapshot.clientFirstName,
      snapshot.clientLastName,
      snapshot.clientLocation,
      snapshot.clientTown,
      snapshot.clientState,
      snapshot.clientZip,
      snapshot.clientPhone,
      snapshot.clientEmail,
    ]
  );

  const vars = useMemo(
    () => buildEmailVars(client, userSettings),
    [client, userSettings]
  );

  // Subject + body templates — same defaults App.tsx falls back to when
  // userSettings doesn't carry a template (App.tsx:1924, 2006).
  const subjectTemplate =
    userSettings?.emailSubjectTemplate || "Your Proposal – {{clientLastName}}";
  const bodyTemplate = userSettings?.emailBodyTemplate || "";

  const renderedSubject = useMemo(
    () => renderTemplate(subjectTemplate, vars),
    [subjectTemplate, vars]
  );
  const renderedBody = useMemo(
    // renderEmailBody (not renderTemplate) so the "Hi {{clientFirstName}},"
    // → "Hi," blank-name cleanup runs on mobile too, matching desktop
    // (App.tsx:2427).
    () => renderEmailBody(bodyTemplate, vars),
    [bodyTemplate, vars]
  );

  // Form state — only re-seed when the sheet OPENS, so user edits aren't
  // wiped if a re-render fires (e.g. parent snapshot re-renders).
  const [to, setTo] = useState(client.clientEmail);
  const [subject, setSubject] = useState(renderedSubject);
  const [body, setBody] = useState(renderedBody);
  const [sendMeCopy, setSendMeCopy] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  useEffect(() => {
    if (!open) return;
    // Fresh seed from the latest props each time the sheet opens.
    setTo(client.clientEmail || "");
    setSubject(renderedSubject);
    setBody(renderedBody);
    setSendMeCopy(false);
    setStatus({ kind: "idle" });
  }, [open, client.clientEmail, renderedSubject, renderedBody]);

  // Lock background scroll while the sheet is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const replyTo = (userSettings?.userEmail || "").trim();
  const trimmedTo = (to || "").trim();
  const canSend =
    status.kind !== "sending" && !!trimmedTo && !!subject.trim();

  const performSend = async () => {
    setStatus({ kind: "sending" });

    // Required guard rails first — friendly messages, no silent failures.
    if (!trimmedTo) {
      setStatus({
        kind: "failed",
        message: "Add a client email in the Customer card first.",
      });
      return;
    }
    // Defense in depth — MobileProposalPage already gates the
    // "Send Proposal" tap on the same check, but if any future
    // path opens the sheet directly we still refuse to send
    // without name / phone / email.
    if (!isSenderInfoComplete(userSettings)) {
      setStatus({
        kind: "failed",
        message: SENDER_INFO_MISSING_MESSAGE,
      });
      return;
    }

    try {
      // Steps 1–4 (resolve line items → org sections → build row payload →
      // idempotent insert/update keyed on estimateId → review link) live in
      // the shared saveProposalAndGetLink so the "Email to myself" flow runs
      // byte-identical logic. proposalNotes is the LIVE textarea state from
      // MobileProposalPage, forwarded so the saved row carries the notes the
      // user actually typed (mobile's estimateName is usually empty, which
      // would otherwise break buildProposalPayload's localStorage fallback).
      const { proposalId: savedId, reviewLink } = await saveProposalAndGetLink(
        supabase,
        {
          snapshot,
          pricingItems,
          estimate,
          userSettings,
          orgId,
          proposalNotes,
        }
      );
      if (!savedId || !reviewLink) {
        setStatus({
          kind: "failed",
          message: "Could not generate a proposal link. Try again.",
        });
        return;
      }

      // 5. Build the email (View Proposal button straight to /review +
      //    company footer) and its matching plain-text part
      const emailArgs = {
        bodyText: body,
        proposalId: savedId,
        footer: emailFooterFromSettings(userSettings),
      };
      const html = buildEmailHtml(emailArgs);
      const text = buildEmailText(emailArgs);

      // 6. If we're offline at send time, drop straight into the queue.
      if (
        typeof navigator !== "undefined" &&
        navigator.onLine === false
      ) {
        enqueueEmail({
          proposalId: savedId,
          to: trimmedTo,
          subject,
          body: html,
          // The edge function rejects a send with no replyTo (400), so a
          // queued item without it could never go out — the whole reason the
          // offline outbox silently lost proposals. Carry cc/text too, so the
          // queued send is identical to the live one.
          replyTo,
          cc: sendMeCopy ? [replyTo] : undefined,
          text,
        });
        setStatus({
          kind: "queued",
          reason:
            "You're offline. The email is saved and will send the next time you're online.",
        });
        if (onSent) onSent({ proposalId: savedId, reviewLink });
        return;
      }

      // 7. Send via edge function — same call desktop makes.
      const { error } = await invokeSendProposalEmail(supabase, {
        to: trimmedTo,
        subject,
        html,
        proposalId: savedId,
        replyTo,
        text,
        cc: sendMeCopy ? [replyTo] : undefined,
      });

      if (error) {
        // Network-class errors → queue. Hard validation errors → surface.
        const msg = String(
          (error as any)?.message ||
            (error as any)?.context?.body ||
            "Unknown send error"
        );
        const transient = /network|failed to fetch|timeout|abort/i.test(msg);
        if (transient) {
          enqueueEmail({
            proposalId: savedId,
            to: trimmedTo,
            subject,
            body: html,
            replyTo,
            cc: sendMeCopy ? [replyTo] : undefined,
            text,
          });
          setStatus({
            kind: "queued",
            reason: `Send failed (${msg}). Saved to the queue — will retry when online.`,
          });
          if (onSent) onSent({ proposalId: savedId, reviewLink });
          return;
        }
        setStatus({ kind: "failed", message: msg });
        return;
      }

      // 8. Copy the link, mirror desktop UX.
      try {
        if (
          typeof navigator !== "undefined" &&
          navigator.clipboard?.writeText
        ) {
          await navigator.clipboard.writeText(reviewLink);
        }
      } catch {
        // Clipboard can refuse on iOS without a direct gesture — ignore.
      }

      setStatus({ kind: "sent", link: reviewLink });
      if (onSent) onSent({ proposalId: savedId, reviewLink });

      // Auto-close after a moment so the user sees the success state.
      setTimeout(() => {
        setStatus((s) => (s.kind === "sent" ? { kind: "idle" } : s));
        onClose();
      }, 1400);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error("[MobileSendSheet] send crash:", err);
      setStatus({
        kind: "failed",
        message: err?.message || String(err) || "Unknown error",
      });
    }
  };

  const handleBackdropClick = () => {
    if (status.kind === "sending") return;
    onClose();
  };

  return (
    <div
      className="m-send-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Send proposal"
      onClick={handleBackdropClick}
    >
      <div className="m-send-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="m-send-grabber" aria-hidden />

        <header className="m-send-head">
          <h2 className="m-send-title">Send Proposal</h2>
          <button
            type="button"
            className="m-send-close"
            onClick={onClose}
            aria-label="Close"
            disabled={status.kind === "sending"}
          >
            ✕
          </button>
        </header>

        <div className="m-send-body">
          <label className="m-send-field">
            <span className="m-send-label">To</span>
            <input
              type="email"
              className="m-send-input"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="email"
              inputMode="email"
              placeholder="client@example.com"
              disabled={status.kind === "sending"}
            />
          </label>

          <label className="m-send-field">
            <span className="m-send-label">Subject</span>
            <input
              type="text"
              className="m-send-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={status.kind === "sending"}
            />
          </label>

          <label className="m-send-field">
            <span className="m-send-label">Message</span>
            <textarea
              className="m-send-textarea"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              spellCheck
              autoCapitalize="sentences"
              autoCorrect="on"
              disabled={status.kind === "sending"}
              placeholder="Personal note (optional)…"
            />
          </label>

          <label className="m-send-toggle-row">
            <input
              type="checkbox"
              checked={sendMeCopy}
              onChange={(e) => setSendMeCopy(e.target.checked)}
              disabled={status.kind === "sending" || !replyTo}
            />
            <span>
              Send me a copy
              {replyTo ? (
                <span className="m-send-toggle-sub"> ({replyTo})</span>
              ) : (
                <span className="m-send-toggle-sub m-send-warn">
                  {" "}
                  (set your email in Settings to enable)
                </span>
              )}
            </span>
          </label>

          {/* Status surface — replaces "what just happened" guesswork. */}
          {status.kind === "sent" ? (
            <div className="m-send-status m-send-status--ok">
              ✓ Sent. Review link copied to clipboard.
              <div className="m-send-status-sub">{status.link}</div>
            </div>
          ) : status.kind === "queued" ? (
            <div className="m-send-status m-send-status--warn">
              {status.reason}
            </div>
          ) : status.kind === "failed" ? (
            <div className="m-send-status m-send-status--err">
              {status.message}
            </div>
          ) : null}
        </div>

        <footer className="m-send-foot">
          <button
            type="button"
            className="m-send-cancel"
            onClick={onClose}
            disabled={status.kind === "sending"}
          >
            Cancel
          </button>
          {status.kind === "failed" ? (
            <button
              type="button"
              className="m-send-primary"
              onClick={performSend}
              disabled={!canSend}
            >
              Retry
            </button>
          ) : status.kind === "queued" ? (
            <button
              type="button"
              className="m-send-primary"
              onClick={onClose}
            >
              Done
            </button>
          ) : (
            <button
              type="button"
              className="m-send-primary"
              onClick={performSend}
              disabled={!canSend}
            >
              {status.kind === "sending"
                ? "Sending…"
                : status.kind === "sent"
                  ? "Sent ✓"
                  : "Send"}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
